import { TFunction } from 'i18next'
import { UUID_PATTERN } from '../../../utils/regex/uuidPattern'

// Type definitions for the route title map structure
interface RoutePattern {
  pattern: RegExp
  title: string
}

interface RoutePatterns {
  patterns: RoutePattern[]
}

type RouteValue = string | RouteMap | RoutePatterns

interface RouteMap {
  [key: string]: RouteValue
}

// Type for the entire route title map
type RouteTitleMap = RouteMap

export const buildRouteTitleMap = (t: TFunction): RouteTitleMap => ({
  '/': t('root'),
  '/tasks': {
    '/': t('tasks.root'),
    '/pending': t('tasks.pending'),
    '/complete': t('tasks.complete'),
    '/rejected': t('tasks.rejected'),
  },
  '/start': {
    '/': t('start.root'),
    '/drafts': t('start.drafts'),
  },
  '/requests': {
    '/': t('requests.root'),
    '/owned': t('requests.owned'),
    '/pending': t('requests.pending'),
    '/delayed': t('requests.delayed'),
    '/completed': t('requests.completed'),
    '/cancelled': t('requests.cancelled'),
    '/rejected': t('requests.rejected'),
    '/mine': t('requests.mine'),
    '/following': t('requests.following'),
    '/paused': t('requests.paused'),
    '/all': t('requests.all'),
    '/detail': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}/overview`),
          title: t('requests.detail.patterns.UUID-overview'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/my-tasks`),
          title: t('requests.detail.patterns.UUID-my-tasks'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/renewal-assessment`),
          title: t('requests.detail.patterns.UUID-renewal-assessment'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/submissions`),
          title: t('requests.detail.patterns.UUID-submissions'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/documents`),
          title: t('requests.detail.patterns.UUID-documents'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/risks`),
          title: t('requests.detail.patterns.UUID-risks'),
        },
        {
          pattern: /\//,
          title: t('requests.detail.patterns.root'),
        },
      ],
    },
  },
  '/suppliers': {
    '/active': t('suppliers.active'),
    '/inactive': t('suppliers.inactive'),
    '/detail': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}/overview`),
          title: t('suppliers.detail.patterns.UUID-overview'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/activity`),
          title: t('suppliers.detail.patterns.UUID-activity'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/documents`),
          title: t('suppliers.detail.patterns.UUID-documents'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/risk-management`),
          title: t('suppliers.detail.patterns.UUID-risk-management'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/contacts`),
          title: t('suppliers.detail.patterns.UUID-contacts'),
        },
        {
          pattern: new RegExp('/'),
          title: t('suppliers.detail.patterns.UUID'),
        },
      ],
    },
  },
  '/hosted-catalogues': {
    patterns: [
      {
        pattern: new RegExp(`${UUID_PATTERN}/browse`),
        title: t('hosted-catalogues.browse'),
      },
    ],
  },
  '/agent-marketplace': t('agent-marketplace'),
  '/subprocessors': t('subprocessors'),
  '/assessments': {
    '/all': t('assessments.all'),
    '/actionNeeded': t('assessments.actionNeeded'),
    '/inProgress': t('assessments.inProgress'),
    '/upcoming': t('assessments.upcoming'),
    '/complete': t('assessments.complete'),
    '/rejected': t('assessments.rejected'),
  },
  '/agreements': {
    '/active': t('agreements.active'),
    '/upcoming': t('agreements.upcoming'),
    '/inactive': t('agreements.inactive'),
    '/oneOff': t('agreements.oneOff'),
    '/detail': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}/overview`),
          title: t('agreements.detail.patterns.UUID-overview'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/submissions`),
          title: t('agreements.detail.patterns.UUID-submissions'),
        },
        {
          pattern: new RegExp(`${UUID_PATTERN}/documents`),
          title: t('agreements.detail.patterns.UUID-documents'),
        },
      ],
    },
  },
  '/renewals': {
    '/all': t('renewals.all'),
    '/actionNeeded': t('renewals.actionNeeded'),
    '/inProgress': t('renewals.inProgress'),
    '/overDeadline': t('renewals.overDeadline'),
    '/upcoming': t('renewals.upcoming'),
    '/complete': t('renewals.complete'),
    '/rejected': t('renewals.rejected'),
    '/paused': t('renewals.paused'),
  },
  '/reporting': {
    '/': t('reporting.root'),
    '/operational': t('reporting.operational'),
    '/spend': t('reporting.spend'),
    '/mine': t('reporting.mine'),
  },
  '/insights': {
    '/': t('insights.root'),
    '/custom-dashboards': t('insights.root'),
  },
  '/risks': {
    '/': t('risks.root'),
    '/all': t('risks.all'),
    '/open': t('risks.open'),
    '/closed': t('risks.closed'),
    '/assigned': t('risks.assigned'),
    '/overdue': t('risks.overdue'),
    '/dueIn30Days': t('risks.dueIn30Days'),
    '/detail': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}`),
          title: t('risks.detail.patterns.UUID'),
        },
      ],
    },
  },
  '/risk-alerts': {
    '/open': t('risk-alerts.open'),
    '/closed': t('risk-alerts.closed'),
  },
  '/documents': {
    '/all': t('documents.all'),
    '/contracts': t('documents.contracts'),
    '/orderForms': t('documents.orderForms'),
    '/msa': t('documents.msa'),
  },
  '/budgets': {
    '/all': t('budgets.all'),
    '/open': t('budgets.open'),
    '/closed': t('budgets.closed'),
  },
  '/settings': {
    '/notifications': t('settings.notifications'),
    '/preferences': t('settings.preferences'),
    '/default-notifications': t('settings.default-notifications'),
    '/ai': t('settings.ai'),
    '/security': t('settings.security'),
    '/core-data': {
      '/': t('settings.core-data.root'),
      '/currencies': t('settings.core-data.currencies'),
      '/subsidiaries': t('settings.core-data.subsidiaries'),
      '/department': t('settings.core-data.department'),
      '/payment-methods': t('settings.core-data.payment-methods'),
      '/payment-terms': t('settings.core-data.payment-terms'),
      '/line-item-types': t('settings.core-data.line-item-types'),
      '/addresses': t('settings.core-data.addresses'),
    },
    '/users-and-groups': {
      '/all': t('settings.users-and-groups.all'),
    },
    '/permissions': {
      '/': t('settings.permissions.root'),
      '/config-access': t('settings.permissions.config-access'),
    },
    '/user-properties': t('settings.user-properties'),
    '/import': t('settings.import'),
    '/audit-log': t('settings.audit-log'),
    '/workflows': t('settings.workflows'),
    '/dynamic-assignees': {
      '/': t('settings.dynamic-assignees.root'),
      '/thresholds': t('settings.dynamic-assignees.thresholds'),
    },
    '/forms': t('settings.forms'),
    '/sourcing': t('settings.sourcing'),
    '/risks': t('settings.risks'),
    '/risk-management': t('settings.risk-management'),
    '/subprocessor-settings': t('settings.subprocessor-settings'),
    '/product-catalogue': {
      '/': t('settings.product-catalogue.root'),
      '/all': t('settings.product-catalogue.all'),
    },
    '/punchout-catalogues': t('settings.punchout-catalogues'),
    '/hosted-catalogues': t('settings.hosted-catalogues'),
    '/document-storage': {
      '/': t('settings.document-storage.root'),
      '/types': t('settings.document-storage.types'),
    },
    '/__feature-flags': t('settings.__feature-flags'),
    '/admin': t('settings.admin'),
    '/integrations': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}`),
          title: t('settings.integrations.patterns.UUID'),
        },
        {
          pattern: new RegExp(''),
          title: t('settings.integrations.patterns.root'),
        },
      ],
    },
  },
  '/workflows': {
    '/edit': {
      patterns: [
        {
          pattern: new RegExp(`${UUID_PATTERN}`),
          title: t('workflows.edit.patterns.UUID'),
        },
      ],
    },
  },
})

export const getTitleFromMap = (pathnameWParams: string, map: RouteTitleMap): string | null => {
  if (pathnameWParams === '/') {
    const rootValue = map['/']
    return typeof rootValue === 'string' ? rootValue : null
  }
  if (!pathnameWParams) return null

  const pathname = pathnameWParams.split('?')[0]
  const segments = (pathname || '')
    .split('/')
    .filter(Boolean)
    .map((segment) => `/${segment}`)
  segments.push('/')
  let current: RouteValue = map

  for (const segment of segments) {
    if (isRouteMap(current) && segment in current) {
      const nextValue: RouteValue | undefined = current[segment]
      if (nextValue !== undefined) {
        current = nextValue
      } else {
        break
      }
    } else if (isRoutePatterns(current)) {
      const match = current.patterns.find(({ pattern }) => pattern.test(pathname || ''))
      return match?.title || null
    } else {
      break
    }
  }

  return typeof current === 'string' ? current : null
}

// Type guard to check if a value is RoutePatterns
function isRoutePatterns(value: RouteValue): value is RoutePatterns {
  return typeof value === 'object' && value !== null && 'patterns' in value && Array.isArray((value as RoutePatterns).patterns)
}

// Type guard to check if a value is RouteMap
function isRouteMap(value: RouteValue): value is RouteMap {
  return typeof value === 'object' && value !== null && !('patterns' in value)
}
